import axios from "axios";

const S2M_URL = process.env.REACT_APP_S2M_API_URL || process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${S2M_URL}/api`;
export const IS_CONFIGURED = Boolean(process.env.REACT_APP_S2M_API_URL);

const TOKEN_KEY = "s2m_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const client = axios.create({ baseURL: API_BASE });

const LOCAL_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const localClient = axios.create({ baseURL: LOCAL_BASE });

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      clearToken();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const errMsg = (e) => {
  const d = e?.response?.data?.detail ?? e?.response?.data?.message ?? e?.message;
  if (Array.isArray(d)) return d.map((x) => x?.msg || (typeof x === "string" ? x : JSON.stringify(x))).join(", ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || "Si è verificato un errore. Riprova.";
};

export const api = {
  // Auth
  login: (username, access_code) =>
    client.post("/auth/login", { username, access_code }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),

  // Programs
  patientProgram: (pid) => client.get(`/programs/patient/${pid}`).then((r) => r.data),
  createProgram: (body) => client.post("/programs", body).then((r) => r.data),

  // Dashboards
  patientStats: (pid) => client.get(`/dashboard/patient/${pid}/stats`).then((r) => r.data),
  physioDashboard: () => client.get("/dashboard/physio").then((r) => r.data),
  adminStats: () => client.get("/admin/stats").then((r) => r.data),

  // Exercises
  exercises: () => client.get("/exercises").then((r) => r.data),
  exercise: (id) => client.get(`/exercises/${id}`).then((r) => r.data),
  createExercise: (body) => client.post("/exercises", body).then((r) => r.data),
  updateExercise: (id, body) => client.put(`/exercises/${id}`, body).then((r) => r.data),
  deleteExercise: (id) => client.delete(`/exercises/${id}`).then((r) => r.data),
  logExercise: (body) => client.post("/exercise-logs", body).then((r) => r.data),

  // Diary
  diary: (pid) => client.get(`/diary/patient/${pid}`).then((r) => r.data),
  createDiary: (body) => client.post("/diary", body).then((r) => r.data),

  // Brace
  braceLogs: (pid) => client.get(`/brace-logs/patient/${pid}`).then((r) => r.data),
  createBraceLog: (body) => client.post("/brace-logs", body).then((r) => r.data),

  // Education
  education: () => client.get("/education").then((r) => r.data),
  createEducation: (body) => client.post("/education", body).then((r) => r.data),
  updateEducation: (id, body) => client.put(`/education/${id}`, body).then((r) => r.data),
  deleteEducation: (id) => client.delete(`/education/${id}`).then((r) => r.data),

  // Messages
  conversation: (otherId) => client.get(`/messages/conversation/${otherId}`).then((r) => r.data),
  sendMessage: (body) => client.post("/messages", body).then((r) => r.data),
  unreadCount: () => client.get("/messages/unread-count").then((r) => r.data),

  // Profile (physio): logo + orari ricevimento chat
  updateLogo: (logo) => client.put("/profile/logo", { logo }).then((r) => r.data),
  getAvailability: () => client.get("/profile/availability").then((r) => r.data),
  updateAvailability: (availability) => client.put("/profile/availability", { availability }).then((r) => r.data),
  physioAvailability: () => client.get("/physio/availability").then((r) => r.data),

  // Patients (physio)
  patients: () => client.get("/patients").then((r) => r.data),
  patient: (id) => client.get(`/patients/${id}`).then((r) => r.data),
  createPatient: (body) => client.post("/patients/create", body).then((r) => r.data),
  updatePatientProfile: (id, body) => client.put(`/patients/${id}/profile`, body).then((r) => r.data),
  patientAssistance: (id) => client.get(`/patients/${id}/assistance-minutes`).then((r) => r.data),
  physioAssistance: (period) => client.get("/physio/assistance-minutes", { params: period ? { period } : {} }).then((r) => r.data),
  adminAssistance: (period) => client.get("/admin/assistance-minutes", { params: period ? { period } : {} }).then((r) => r.data),

  // Brace prescription (ore prescritte) — LOCAL backend
  getBracePrescription: (pid) => localClient.get(`/brace-prescription/${pid}`).then((r) => r.data),
  setBracePrescription: (pid, prescribed_hours) => localClient.put(`/brace-prescription/${pid}`, { prescribed_hours }).then((r) => r.data),
  allBracePrescriptions: () => localClient.get(`/brace-prescriptions`).then((r) => r.data),

  // Admin
  adminUsers: () => client.get("/admin/users").then((r) => r.data),
  createPhysio: (body) => client.post("/admin/physios", body).then((r) => r.data),
  deletePhysio: (id) => client.delete(`/admin/physios/${id}`).then((r) => r.data),
  deletePatient: (id) => client.delete(`/patients/${id}`).then((r) => r.data),
  resetPassword: (id) => client.post(`/admin/users/${id}/reset-password`).then((r) => r.data),
  pendingContent: () => client.get("/admin/pending-content").then((r) => r.data),
  reviewExercise: (id, body) => client.post(`/admin/exercises/${id}/review`, body).then((r) => r.data),
  reviewArticle: (id, body) => client.post(`/admin/articles/${id}/review`, body).then((r) => r.data),
};

export default client;
